import { diffChars, diffLines } from 'diff';
import { BlsGmbDifference } from '../../models/locations-models/bls-gmb-difference';
import { calculateGeoPointDistance, coordinateStringToObj } from '../../utils/map/turf-utils';
import { round } from '@turf/helpers';
import { LSCField } from '../../models/LSCFields';
import { DifferenceActions } from './enums/difference-actions';
import { ALTERNATIVE_FIELD_IDS } from './constants';
import { isStringAnUrl } from '../../core/utilities';

// TODO: Refactor Diff map and respective utils such as this one when doing difference/map component(s) overhaul

export enum GmbDiffMarkerType {
  VERIFIED = 'V',
  GMB = 'B'
}

enum DifferenceType {
  ADDED,
  REMOVED,
  SAME
}

type ProcessedDifference = [DifferenceType, string];

enum DifferenceHtmlNodeType {
  ins = 'ins',
  del = 'del',
  span = 'span'
}

export enum DiffCheckingStrategy {
  chars,
  lines
}

export interface DiffLine {
  html: string;
  lscUrl: string | null;
  gmbUrl: string | null;
}

/**
 * Checks if the value of a field is a list and should therefore be split up by ',' into different lines.
 * @param fieldName Name of the field to check e.g. 'specialHours'
 */
export const shouldValueBeSplit = (fieldName: string): boolean => {
  return fieldName === LSCField.specialHours ||
      fieldName === LSCField.otherPhotos ||
      fieldName === LSCField.categories ||
      fieldName === LSCField.additionalCategory ||
      fieldName === ALTERNATIVE_FIELD_IDS[LSCField.additionalCategory] ||
      fieldName === LSCField.labels ||
      fieldName.includes('Coordinate') ||
      fieldName.includes('coordinate') ||
      fieldName === 'address' ||
      fieldName === LSCField.additionalPhone ||
      fieldName === ALTERNATIVE_FIELD_IDS[LSCField.additionalPhone] ||
      fieldName === LSCField.regularHours ||
      fieldName === LSCField.businessHours ||
      fieldName === LSCField.customServices ||
      fieldName === LSCField.appointmentURL ||
      fieldName === ALTERNATIVE_FIELD_IDS[LSCField.appointmentURL] ||
      fieldName === LSCField.orderAheadURL ||
      fieldName === ALTERNATIVE_FIELD_IDS[LSCField.orderAheadURL] ||
      fieldName === LSCField.reservationsURL ||
      fieldName === ALTERNATIVE_FIELD_IDS[LSCField.reservationsURL] ||
      fieldName === 'latLng' ||
      fieldName === LSCField.socialMediaUrls;
};

/**
 * Returns a formatted string representation of difference.customServices values
 */
export const getCustomServicesString = (jsonString: string): string | null => {
  try {
    const customServices: [{ serviceName: string, serviceDescription: string | null, serviceCategoryId: string | null }] =
        // create a valid JSON
        JSON.parse(jsonString.replace(/'/g, '"'));
    let returnStr = '';
    customServices.forEach((service, index) => {
      returnStr = returnStr + service.serviceName
          + (service.serviceCategoryId ? ' (' + service.serviceCategoryId + ')' : '')
          + (service.serviceDescription ? ': ' + service.serviceDescription : '')
          + (index !== customServices.length - 1 ? '\r\n\r\n' : '');
    });
    return customServices.length ? returnStr : null;
  } catch (err) {
    console.error(err); // error in the above string
    return jsonString;
  }
};

/**
 * Prettifies a field value depending on the `fieldId` such that it can be effectively diff-checked
 * afterwards.
 * @param fieldId Determines how to prettify the value.
 * @param value Value to prettify.
 */
export const prepareForDiffChecking = (fieldId: LSCField | string, value: string): string => {
  if (!value?.length) {
    // nothing to diff-check
    return '';
  }

  // remove curly braces at the start and end of Diff and BLS Update strings for 'socialMediaUrls'
  if (fieldId === LSCField.socialMediaUrls) {
    value = value.replace('{', '').replace('}', '');
  }

  let lines: string[] = [];

  if (shouldValueBeSplit(fieldId)) {
    let splitValues = [];

    if (fieldId === 'customServices') {
      if (getCustomServicesString(value)) {
        splitValues = getCustomServicesString(value).split('\n\n');
      }
    } else {
      const splitBy = fieldId === 'address'
          // the address components are to be split by newline
          ? '\n'
          // everything else can be separated by "," or ", "
          : /,\s?/;
      splitValues = value.split(splitBy);
    }

    for (let i = 0; i < splitValues.length; i++) {
      let line: string = splitValues[i];

      const isEmpty = !line?.length;
      const isLast = i === splitValues.length - 1;

      // special case for addresses
      if (fieldId === 'address') {
        line =
            isEmpty
                // fallback so that the line isn't shown as whitespace
                // --> we want to clearly show it as empty
                ? '-'
                : isLast ? line : `${line},`;
        lines.push(line);
      } else if ([
        'otherPhotos',
        'url_appointment',
        'url_order_ahead',
        'url_reservations',
        'customServices'].includes(fieldId)
      ) {
        lines.push(line.trim());
      } else {
        lines.push(isLast ? line : `${line},`);
      }
    }
  } else {
    lines.push(value);
  }

  // Join the resulting lines with a linebreak so the whole resulting
  // string can be diff-checked as a standalone, pre-formatted block.
  return lines.join('\n');
};

/**
 * A helper to convert diffs of the form:
 * [
 *   [DifferenceType.ADDED, "http://www.salam"],
 *   [DifferenceType.REMOVED, "2021-10-26: x,"],
 *   ...
 * ]
 * into html-ready strings:
 * `
 * <ins>http://www.salam</ins>
 * <del>2021-10-26: x,</del>
 * `
 * with a fallback to `<span>`
 * @param textDiffs
 */
export const textDiffsToHtml = (textDiffs: ProcessedDifference[]): string => {
  let htmlString = '';

  for (const diff of textDiffs) {
    const [diffType, content] = diff;

    const node: DifferenceHtmlNodeType = (() => {
      switch (diffType) {
        case DifferenceType.ADDED:
          return DifferenceHtmlNodeType.ins;
        case DifferenceType.REMOVED:
          return DifferenceHtmlNodeType.del;
        default:
          return DifferenceHtmlNodeType.span;
      }
    })();

    htmlString += `<${node}>${content}</${node}>`;
  }

  return htmlString;
};

/**
 * Field-agnostic diff-checking based on the `DiffCheckingStrategy`.
 * @param before
 * @param after
 * @param strategy
 * @returns ProcessedDifference[]
 */
export const performDiffChecking = (
    before: string,
    after: string,
    strategy: DiffCheckingStrategy,
): ProcessedDifference[] => {
  const textDiffs: ProcessedDifference[] = [];

  // BLS-3672 using a more human-friendly diff-checking approach than `diff-match-patch`.
  // Since we're already comparing line-by-line, we can go for char diffs.
  const actualDiffs = strategy === DiffCheckingStrategy.chars
      ? diffChars(before, after)
      : diffLines(before, after);

  for (const part of actualDiffs) {
    const { added, removed, value } = part;

    const differenceType: DifferenceType = (() => {
      switch (true) {
        case added:
          return DifferenceType.ADDED;
        case removed:
          return DifferenceType.REMOVED;
        default:
          return DifferenceType.SAME;
      }
    })();

    textDiffs.push([differenceType, value]);
  }

  return textDiffs;
};

/**
 * Returns an array of structured diff lines.
 * Each line contains the HTML diff and extracted URLs for LSC and GMB.
 * @param gmbValue
 * @param blsValue
 * @param fieldId
 * @param strategy
 */
export const getStructuredDifferenceLines = (
    {
      gmbValue,
      blsValue,
      fieldId,
      strategy,
    }: {
      gmbValue: string,
      blsValue: string,
      fieldId: LSCField | string,
      strategy?: DiffCheckingStrategy,
    },
): DiffLine[] => {
  const resolvedStrategy = strategy ?? (
      shouldValueBeSplit(fieldId) ? DiffCheckingStrategy.lines : DiffCheckingStrategy.chars
  );
  let textDiffs: ProcessedDifference[] = [];

  // if both values exist (are not null)
  if (gmbValue && blsValue) {
    while (gmbValue.startsWith(' ') && blsValue.startsWith(' ')) {
      gmbValue = gmbValue.substring(1);
      blsValue = blsValue.substring(1);
    }

    if (fieldId === 'hasServiceArea' || fieldId === 'openInfo.status') {
      // special case for the boolean `hasServiceArea`  or `openInfo.status` enum values...
      // -> showing blsValue as fully inserted & gmbValue as fully deleted
      textDiffs = [
        [DifferenceType.ADDED, blsValue],
        [DifferenceType.REMOVED, gmbValue],
      ];
    } else {
      textDiffs = performDiffChecking(gmbValue, blsValue, resolvedStrategy);
    }
  } else if (blsValue) {
    while (blsValue.startsWith(' ')) {
      blsValue = blsValue.substring(1);
    }
    textDiffs = [[DifferenceType.ADDED, blsValue]];
  } else if (gmbValue) {
    while (gmbValue.startsWith(' ')) {
      gmbValue = gmbValue.substring(1);
    }
    textDiffs = [[DifferenceType.REMOVED, gmbValue]];
  }

  const lines: DiffLine[] = [];

  let currentLineDiffs: ProcessedDifference[] = [];
  let currentLscRaw = '';
  let currentGmbRaw = '';

  const flushLine = () => {
    const extractUrl = (raw: string): string | null => {
      let cleaned = raw.trim();
      if (!cleaned) return null;

      // Strip trailing comma if it was added during prepareForDiffChecking
      if (cleaned.endsWith(',')) {
        cleaned = cleaned.slice(0, -1).trim();
      }

      if (cleaned.includes('=')) {
        const parts = cleaned.split('=');
        const valueCandidate = parts.slice(1).join('=').trim();
        return isStringAnUrl(valueCandidate) ? valueCandidate : null;
      }
      return isStringAnUrl(cleaned) ? cleaned : null;
    };

    lines.push({
      html: textDiffsToHtml(currentLineDiffs),
      lscUrl: extractUrl(currentLscRaw),
      gmbUrl: extractUrl(currentGmbRaw),
    });
    currentLineDiffs = [];
    currentLscRaw = '';
    currentGmbRaw = '';
  };

  for (const [type, content] of textDiffs) {
    const parts = content.split('\n');

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];

      if (part.length > 0 || i < parts.length - 1) {
        currentLineDiffs.push([type, part]);
        if (type !== DifferenceType.REMOVED) currentLscRaw += part;
        if (type !== DifferenceType.ADDED) currentGmbRaw += part;
      }

      if (i < parts.length - 1) {
        flushLine();
      }
    }
  }

  if (currentLineDiffs.length > 0 || currentLscRaw || currentGmbRaw) {
    flushLine();
  }

  return lines;
};

/**
 * Prepare html-ready differences by taking into account the `fieldId`.
 * @param gmbValue
 * @param blsValue
 * @param fieldId
 * @param strategy
 */
export const getFormattedDifferences = (
    {
      gmbValue,
      blsValue,
      fieldId,
      strategy,
    }: {
      gmbValue: string,
      blsValue: string,
      // TODO Add handling for `googleUpdateValue`; unused for now
      googleUpdateValue?: string,

      fieldId: LSCField | string,

      // BLS-3672 add github-like diffing by first checking for char diffs
      // and then for line diffs
      strategy?: DiffCheckingStrategy,
    },
) => {
  const resolvedStrategy = strategy ?? (
      shouldValueBeSplit(fieldId) ? DiffCheckingStrategy.lines : DiffCheckingStrategy.chars
  );
  const lines = getStructuredDifferenceLines({
    gmbValue,
    blsValue,
    fieldId,
    strategy: resolvedStrategy,
  });
  return lines.map(line => line.html).join('\n');
};

/**
 * Highlight rows extracted from content already run through char-based diff-checker.
 * Similar to what bitbucket and github are doing in PR commit comparison.
 * Introduced in BLS-3672 and after brainstorming w/ M. Hirsch.
 * NOT USED RIGHT NOW -- this would work best if we had a third column where ONLY
 * the diffs are displayed -- the LSC and GBP columns would stay as they are...
 * @param charBasedDiffs
 * @returns string
 */
export const highlightRowsContainingChanges = (charBasedDiffs: string) => {
  // The `diff` package will produce a span-wrapped html-ready string
  // with spans also placed before and after any suitable <ins> and <del> tags
  // To identify and highlight rows which do include some diffs (i.e. ins/del tags),
  // let's first get rid of these spans.
  const genericSpanRe = /<[^>]?span>?/gm; // https://regex101.com/r/oC2oU2/1
  const contentWithoutSpans = charBasedDiffs.replace(genericSpanRe, '');

  // https://regex101.com/r/r9eXpy/1
  const onlyModifiedContent = contentWithoutSpans.match(/<(ins|del)>([\s\S]*?)<\/(ins|del)>/gm)?.join('');

  // let's pretend we're about to render the content row-by-row
  const lines = contentWithoutSpans.split('\n');

  const enrichedLines: string[] = [];

  const hasTagsRe = /<\/?(ins|del)>/g;

  for (const line of lines) {
    if (
        // if the line contains a diff, it was modified
        line.match(hasTagsRe)
        // if there are no direct tags, the line may still be in between two tags
        || onlyModifiedContent.includes(line)
    ) {
      enrichedLines.push(`<span class="was-modified">${line}</span>`);
      continue;
    }
    enrichedLines.push(line);
  }

  // join back to return a html-ready string with enriched diff lines
  return enrichedLines.join('\n');
};

/**
 * Reusable helper for cancelling event bubbling
 * @param event
 * @private
 */
export const wasOpenUrlClicked = (event: MouseEvent) => {
  return (event.target as Element)?.classList?.contains('open-url-element');
};

/**
 * Check for existence of http prefix to ensure link viability.
 * @param hrefCandidate
 * @deprecated - use 'prependProtocol' pipe
 */
export const prepareUrlHref = (hrefCandidate: string): string => {
  if (hrefCandidate.startsWith('http')) {
    return hrefCandidate;
  }

  return `http://${hrefCandidate}`;
};

/**
 * Flushes the other, non-active differences in-place.
 * @param difference
 * @param activeAction
 * @private
 */
export const flushRemainingDiffs = (difference: BlsGmbDifference, activeAction: DifferenceActions): void => {
  switch (activeAction) {
    case DifferenceActions.updateGmb:
      difference.acceptDifferencesSelected = false;
      difference.acceptGoogleValueSelected = false;
      return;
    case DifferenceActions.acceptDifference:
      difference.gmbUpdateSelected = false;
      difference.acceptGoogleValueSelected = false;
      return;
    case DifferenceActions.acceptGoogleUpdateValue:
      difference.gmbUpdateSelected = false;
      difference.acceptDifferencesSelected = false;
      return;
  }
};

/**
 * Parse the coordinates, calculate the distance, and construct address list locations mocks
 * to display them on an interactive map.
 * Implements BLS-660
 * @param difference
 */
export const prepareGmbDiffMap = (difference: BlsGmbDifference) => {
  try {
    // Note -- the filtering for only-verified coordinates happened already in the BE.
    // We're sure at this point that the imported coords are truly also verified so we'll display the V-pin.

    // Note #2 -- the diff values come in a string of the form "lat, lon". When they're
    // transformed to a `BlsGmbDifference`, they'll have been split into multiple lines
    // so let's first join the lines and then parse the coordinate strings to do geo math.
    const importedAndVerifiedCoords = coordinateStringToObj(difference.blsValue);
    const gmbCoords = coordinateStringToObj(difference.gmbValue);

    let prettyDistance: string;
    // in meters
    let distanceInMeters = round(calculateGeoPointDistance(importedAndVerifiedCoords, gmbCoords), 0);
    if (distanceInMeters > 1000) {
      prettyDistance = `${round(distanceInMeters / 1000, 1)} km`; //Google also shows km distances with one digit on maps.google.com
    } else {
      prettyDistance = `${distanceInMeters} m`;
    }

    // Prepare two bare-minimum locations because these are what the map service(s)
    // are used to working with. We only need two params -- the `uuid` for knowing which letter to
    // display in the pin, plus the pin's position.
    const locations = [
      {
        uuid: GmbDiffMarkerType.VERIFIED,
        coordinate: importedAndVerifiedCoords,
      },
      {
        uuid: GmbDiffMarkerType.GMB,
        coordinate: gmbCoords,
      },
    ];

    return {
      distance: prettyDistance,
      locations,
    };
  } catch (e) {
    console.warn('Gracefully failed to prepareGmbDiffMap', { e });
    return null;
  }
};
